import maplibregl from "maplibre-gl";
import { deferAfterMapFrame, finiteMapCenter } from "../../shared/map-frame.ts";
import {
  HOME_QUEST_FIT_CAMERA_PAD_FUDGE_PX,
  HOME_QUEST_FIT_EDGE_PAD,
  homeQuestCameraFitPadding,
  homeQuestFitChrome,
  homeSpotQuestFitPoints,
} from "../../shared/home-quest-map-fit.ts";
import type { RoutePaintFeature } from "../../shared/quest-route-paint.ts";
import { clearViewportRect, panOffsetToCenterPoint } from "../../shared/map-view-pan.ts";
import { NORTH_EPSILON_DEG, normalizeBearing, type NorthView } from "../../shared/north-toggle.ts";
import { fitRotatedClear, type Insets, type ScreenRect } from "../../shared/rotated-fit.ts";
import {
  QUEST_SPOT_DOM_FIT_MAX_ZOOM,
  QUEST_SPOT_DOM_SINGLE_ZOOM,
  type SpotFitPin,
} from "../../shared/quest-spot-map-fit.ts";
import type { LngLat } from "../../shared/types.ts";

/** Ease-out with speed on the first frame. The library default bezier starts flat, so the map looks still. */
function questViewEase(t: number): number {
  const u = 1 - t;
  return 1 - u * u;
}

/** A selected quest's start view: the route fitted at its canvas bearing. */
export const QUEST_VIEW_MAX_ZOOM = 16;
export const QUEST_VIEW_MS = 600;
/** Home list card pan-to-start at the current zoom (must finish before the highlight marker is shown). */
export const REVEAL_QUEST_PAN_MS = 350;
/** Clear-viewport margin around the panels when centering a quest start. */
export const REVEAL_MARGIN_PX = 24;
const CLUSTER_EXPAND_MS = 400;
const KEY_PAN_PX = 100;
const KEY_MOVE_MS = 300;
const FIT_MS = 450;
const ISSUE_FOCUS_MS = 500;
/** If moveend never fires while the map reports moving, force-stop (must exceed longest ease). */
const STILL_WATCHDOG_MS = QUEST_VIEW_MS + 400;

const NO_INSETS: Insets = { top: 0, right: 0, bottom: 0, left: 0 };

/** Center, zoom, bearing, and pitch captured before entering route edit. */
export type SavedMapView = {
  center: LngLat;
  zoom: number;
  bearing: number;
  pitch: number;
};
/** Gap without wheel events before the next gesture may preempt programmatic motion again. */
const WHEEL_GESTURE_GAP_MS = 150;

/**
 * Every programmatic camera move on the home/editor map: quest framing, list-card centering,
 * cluster expansion, keyboard pan/zoom, and edit-mode fits. Manual browse pan uses the transform
 * only after {@link armViewNudge}; it publishes via {@link snapView}.
 *
 * All MapLibre camera APIs (`stop`, `jumpTo`, `easeTo`, `panBy`, `flyTo`, `fitBounds`) run on a
 * single serialized queue outside render/ease frames so overlapping eases cannot corrupt center.
 */
export class MapCamera {
  private insets: () => Insets = () => NO_INSETS;
  private obstacles: () => ScreenRect[] = () => [];
  private questRoute: LngLat[] | null = null;
  private questRoutePaint: readonly RoutePaintFeature[] | null = null;
  private framed = false;
  private lastFitKey = "";
  private lastCanvasSizeKey = "";
  private revealGen = 0;
  /** Bumped when the user takes over the camera; queued {@link runCamera} jobs check this before running. */
  private cameraGen = 0;
  private readonly map: maplibregl.Map;
  private savedCenter: LngLat | null = null;
  private tail: Promise<void> = Promise.resolve();
  private programmaticBusy = 0;
  private browseGestureActive: () => boolean = () => false;
  private userGesturePreempted = false;
  private wheelGestureEndTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
  /** Set on the map `load` event. `isStyleLoaded()` can stay false after that. */
  private mapReady = false;

  constructor(map: maplibregl.Map) {
    this.map = map;
    this.map.on("load", () => {
      this.mapReady = true;
    });
    if (this.map.isStyleLoaded()) this.mapReady = true;
    this.map.on("moveend", () => this.rememberCenter());
    this.map.on("movestart", (e) => {
      if (!e.originalEvent) return;
      this.onUserMoveStart();
    });
  }

  /** Panels over the map, in px from each edge, kept clear when framing a quest. */
  setInsets(insets: () => Insets): void {
    this.insets = insets;
  }

  /** Corner overlays (a short detail card, a control stack) the fit must stay clear of. */
  setObstacles(obstacles: () => ScreenRect[]): void {
    this.obstacles = obstacles;
  }

  insetsNow(): Insets {
    return this.insets();
  }

  setQuestRoute(
    route: LngLat[] | null,
    changed: boolean,
    paint: readonly RoutePaintFeature[] | null = null,
  ): void {
    this.questRoute = route;
    this.questRoutePaint = paint;
    if (changed) {
      this.lastFitKey = "";
      this.lastCanvasSizeKey = "";
    }
  }

  isFramed(): boolean {
    return this.framed;
  }

  setFramed(framed: boolean): void {
    this.framed = framed;
  }

  /** Home browse pointer drag/rotate — defer list-card pans until the gesture ends. */
  setBrowseGestureActive(active: () => boolean): void {
    this.browseGestureActive = active;
  }

  /** True while a queued programmatic camera job is in flight (including its ease). */
  programmaticMotionActive(): boolean {
    return this.programmaticBusy > 0;
  }

  /** Resolves when all queued camera jobs (including eases) have finished. */
  whenIdle(): Promise<void> {
    return this.tail;
  }

  private rememberCenter(): void {
    try {
      const c = this.map.getCenter();
      if (finiteMapCenter(c)) this.savedCenter = [c.lng, c.lat];
    } catch {
      /* ignore */
    }
  }

  private safeCenter(): maplibregl.LngLat | null {
    try {
      const c = this.map.getCenter();
      return finiteMapCenter(c) ? c : null;
    } catch {
      return null;
    }
  }

  /** Restore a last-known-good center when MapLibre left the transform invalid. */
  private repairTransform(): void {
    if (!this.savedCenter) return;
    try {
      const c = this.safeCenter();
      if (c) return;
      this.map.jumpTo({
        center: this.savedCenter,
        zoom: this.map.getZoom(),
        bearing: this.map.getBearing(),
        pitch: this.map.getPitch(),
      });
    } catch {
      /* never throw from repair */
    }
  }

  /** Serialize camera mutations — never call MapLibre camera APIs from inside move/render. */
  private runCameraJob(job: () => void): void {
    this.rememberCenter();
    if (!this.safeCenter()) {
      this.repairTransform();
      if (!this.safeCenter()) return;
    }
    job();
  }

  private runCamera(job: () => void): void {
    const gen = this.cameraGen;
    this.tail = this.tail.then(
      () =>
        new Promise<void>((resolve) => {
          this.programmaticBusy += 1;
          this.runWhenStill(() => {
            deferAfterMapFrame(() =>
              deferAfterMapFrame(() => {
                try {
                  if (gen === this.cameraGen) this.runCameraJob(job);
                } catch {
                  this.repairTransform();
                }
                this.runWhenStill(() => {
                  this.programmaticBusy -= 1;
                  resolve();
                });
              }),
            );
          });
        }),
    );
  }

  /**
   * Stop or preempt in-flight motion without waiting for the current ease to finish first.
   * `sync` calls the job in this turn, before the click handler continues, so `easeTo` schedules
   * its frame before later microtasks. The deferred path stays for calls that may be inside a
   * move/render handler.
   */
  private runCameraInterrupt(job: () => void, sync = false): void {
    const gen = this.cameraGen;
    const exec = (): void => {
      try {
        if (gen === this.cameraGen) {
          this.rememberCenter();
          job();
        }
      } catch {
        this.repairTransform();
      }
    };
    if (sync) {
      this.programmaticBusy += 1;
      exec();
      this.tail = this.tail.then(
        () =>
          new Promise<void>((resolve) => {
            this.runWhenStill(() => {
              this.programmaticBusy -= 1;
              resolve();
            });
          }),
      );
      return;
    }
    this.tail = this.tail.then(
      () =>
        new Promise<void>((resolve) => {
          this.programmaticBusy += 1;
          deferAfterMapFrame(() => {
            exec();
            this.runWhenStill(() => {
              this.programmaticBusy -= 1;
              resolve();
            });
          });
        }),
    );
  }

  private runWhenStill(run: () => void): void {
    const attempt = (): void => {
      if (!this.map.isMoving() && !this.map.isEasing()) {
        deferAfterMapFrame(run);
        return;
      }
      let settled = false;
      const finish = (): void => {
        if (settled) return;
        settled = true;
        deferAfterMapFrame(() => {
          if (!this.map.isMoving() && !this.map.isEasing()) run();
          else attempt();
        });
      };
      this.map.once("moveend", finish);
      globalThis.setTimeout(() => {
        if (settled) return;
        finish();
      }, STILL_WATCHDOG_MS);
    };
    attempt();
  }

  private stopNow(): void {
    if (!this.map.isMoving() && !this.map.isEasing()) return;
    try {
      this.map.stop();
    } catch {
      this.repairTransform();
    }
  }

  stopIfAnimating(): void {
    this.runCameraInterrupt(() => this.stopNow());
  }

  /**
   * Before manual transform pan: wait until programmatic motion finishes, then call `onReady`
   * when the camera is safe for `nudgeView` / `_update` (never `stop()` mid-ease).
   */
  armViewNudge(onReady: () => void): void {
    this.runCamera(() => {
      const ready = (): void => {
        if (this.map.isMoving() || this.map.isEasing()) {
          this.map.once("moveend", () => deferAfterMapFrame(ready));
          return;
        }
        deferAfterMapFrame(() => {
          try {
            onReady();
          } catch {
            /* gesture hook must not throw */
          }
        });
      };
      ready();
    });
  }

  /** Publish transform pan once (single moveend). */
  snapView(): void {
    this.runCamera(() => {
      try {
        if (this.map.isMoving() || this.map.isEasing()) return;
        const center = this.safeCenter();
        if (!center) {
          this.repairTransform();
          return;
        }
        this.map.jumpTo({
          center,
          bearing: this.map.getBearing(),
          pitch: this.map.getPitch(),
        });
      } catch {
        this.repairTransform();
      }
    });
  }

  fitQuest(
    bearing: number,
    duration = QUEST_VIEW_MS,
    beforeMove?: () => void,
    spotPins?: readonly SpotFitPin[] | null,
    onComplete?: () => void,
    afterQueued?: () => void,
  ): boolean {
    const route = this.questRoute;
    if (!route) return false;
    this.framed = true;
    const box = this.map.getContainer();
    const w = box.clientWidth;
    const h = box.clientHeight;
    const spotMode = Boolean(spotPins?.length);
    const panelInsets = this.insets();
    const spotPoints = spotMode ? homeSpotQuestFitPoints(spotPins!) : [];
    const fitKey = `${w}x${h}:${JSON.stringify(panelInsets)}:spot=${spotMode ? spotPoints.length : 0}`;
    if (duration === 0 && fitKey === this.lastFitKey) return false;
    const done = (): void => {
      try {
        onComplete?.();
      } catch {
        /* caller hook */
      }
    };
    let started = false;
    const launch = (): void => {
      if (started) return;
      started = true;
      this.runCameraInterrupt(() => {
        const boxNow = this.map.getContainer();
        const wNow = boxNow.clientWidth;
        const hNow = boxNow.clientHeight;
        const insetsNow = this.insets();
        const points = spotMode ? (spotPoints.length ? spotPoints : route) : route;
        const titles = spotMode ? spotPins!.map((pin) => pin.title) : null;
        const view = ((): { center: LngLat; zoom: number; bearing: number } | null => {
          if (!points.length || wNow === 0 || hNow === 0) return null;
          if (spotMode && points.length === 1) {
            return { center: points[0]!, zoom: QUEST_SPOT_DOM_SINGLE_ZOOM, bearing };
          }
          const padding = homeQuestCameraFitPadding(insetsNow, wNow, hNow, spotMode, this.questRoutePaint, titles);
          const chrome = homeQuestFitChrome(spotMode, wNow, hNow, this.questRoutePaint, titles);
          const maxZoom = spotMode ? QUEST_SPOT_DOM_FIT_MAX_ZOOM : QUEST_VIEW_MAX_ZOOM;
          return fitRotatedClear(points, bearing, {
            width: wNow,
            height: hNow,
            padding,
            maxZoom,
            obstacles: this.obstacles(),
            chrome,
            clearance: HOME_QUEST_FIT_EDGE_PAD,
            // The 40px margin has 3px of slack. A bigger nudge recenters the quest.
            maxCenterShift: HOME_QUEST_FIT_CAMERA_PAD_FUDGE_PX,
          });
        })();
        beforeMove?.();
        if (!view) {
          this.map.jumpTo({ bearing, pitch: 0 });
          done();
        } else if (duration > 0) {
          this.map.once("moveend", done);
          this.map.easeTo({ ...view, pitch: 0, duration, easing: questViewEase });
        } else {
          this.map.jumpTo({ ...view, pitch: 0 });
          done();
        }
        this.lastFitKey = `${wNow}x${hNow}:${JSON.stringify(insetsNow)}:spot=${spotMode ? spotPoints.length : 0}`;
        this.lastCanvasSizeKey = `${wNow}x${hNow}`;
        // After easeTo has scheduled its frame, so route setup cannot share that first paint.
        afterQueued?.();
      }, true);
    };
    if (this.canEaseNow()) launch();
    else {
      const onStyle = (): void => {
        if (!this.canEaseNow()) return;
        this.map.off("styledata", onStyle);
        launch();
      };
      this.map.on("styledata", onStyle);
      this.map.once("load", () => {
        this.mapReady = true;
        this.map.off("styledata", onStyle);
        launch();
      });
    }
    return true;
  }

  /** True once the map has loaded, even when `isStyleLoaded()` stays false. */
  private canEaseNow(): boolean {
    if (this.mapReady || this.map.isStyleLoaded()) return true;
    try {
      return typeof this.map.loaded === "function" && this.map.loaded();
    } catch {
      return false;
    }
  }

  canvasResized(width: number, height: number): boolean {
    if (!this.questRoute || !this.framed) return false;
    const sizeKey = `${width}x${height}`;
    if (sizeKey === this.lastCanvasSizeKey) return false;
    this.lastCanvasSizeKey = sizeKey;
    return true;
  }

  /** Record canvas size without triggering a quest refit (used while a selection fit is in flight). */
  ackCanvasSize(width: number, height: number): void {
    if (!this.questRoute || !this.framed) return;
    this.lastCanvasSizeKey = `${width}x${height}`;
  }

  /** Invalidate pending reveal callbacks without stopping the camera (pointer-down uses this). */
  cancelReveal(): void {
    this.revealGen += 1;
  }

  private markUserGestureWindow(): void {
    if (this.wheelGestureEndTimer != null) globalThis.clearTimeout(this.wheelGestureEndTimer);
    this.wheelGestureEndTimer = globalThis.setTimeout(() => {
      this.userGesturePreempted = false;
      this.wheelGestureEndTimer = null;
    }, WHEEL_GESTURE_GAP_MS);
  }

  /** Drop queued programmatic jobs before MapLibre scrollZoom runs (never {@link map.stop} here). */
  yieldBeforeNativeWheel(): void {
    this.cameraGen += 1;
    this.cancelReveal();
    this.markUserGestureWindow();
  }

  /** End in-flight programmatic eases once a user move has started (movestart carries originalEvent). */
  private onUserMoveStart(): void {
    this.markUserGestureWindow();
    if (this.userGesturePreempted || this.programmaticBusy <= 0) return;
    if (!this.map.isMoving() && !this.map.isEasing()) return;
    this.userGesturePreempted = true;
    try {
      this.rememberCenter();
      this.stopNow();
      this.repairTransform();
    } catch {
      this.repairTransform();
    }
  }

  /** @deprecated Use {@link yieldBeforeNativeWheel}; kept for tests. */
  preemptNativeWheel(): void {
    this.yieldBeforeNativeWheel();
  }

  /** Stop in-flight programmatic motion (pointer capture, user zoom, etc.). */
  abortMotion(): void {
    this.runCameraInterrupt(() => this.stopNow());
  }

  revealPoint(
    at: LngLat,
    opts: { delayMs?: number; onArrive?: () => void; view?: NorthView } = {},
  ): void {
    const gen = ++this.revealGen;
    const live = (): boolean => gen === this.revealGen;
    const arrive = (): void => {
      if (live()) opts.onArrive?.();
    };
    if (!Number.isFinite(at[0]) || !Number.isFinite(at[1])) {
      arrive();
      return;
    }
    const pan = (): void => {
      if (!live()) return;
      if (this.browseGestureActive()) {
        this.runCamera(pan);
        return;
      }
      const box = this.map.getContainer();
      const w = box.clientWidth;
      const h = box.clientHeight;
      if (w === 0 || h === 0) {
        arrive();
        return;
      }
      try {
        const clear = clearViewportRect(w, h, this.insets(), REVEAL_MARGIN_PX);
        const p = this.map.project(at);
        const [dx, dy] = panOffsetToCenterPoint(p.x, p.y, clear);
        const target = opts.view;
        const targetBearing = target ? normalizeBearing(target.bearing) : null;
        const targetPitch = target?.pitch ?? 0;
        const turn =
          targetBearing !== null &&
          (Math.abs(normalizeBearing(targetBearing - this.map.getBearing())) >= NORTH_EPSILON_DEG ||
            Math.abs(targetPitch - this.map.getPitch()) >= NORTH_EPSILON_DEG);
        if (!turn && (!Number.isFinite(dx) || !Number.isFinite(dy) || (Math.abs(dx) < 1 && Math.abs(dy) < 1))) {
          arrive();
          return;
        }
        this.map.once("moveend", arrive);
        if (turn) {
          const cx = (clear.left + clear.right) / 2;
          const cy = (clear.top + clear.bottom) / 2;
          this.map.easeTo({
            center: at,
            zoom: this.map.getZoom(),
            bearing: targetBearing!,
            pitch: targetPitch,
            offset: [cx - w / 2, cy - h / 2],
            duration: REVEAL_QUEST_PAN_MS,
          });
        } else {
          this.map.panBy([dx, dy], { duration: REVEAL_QUEST_PAN_MS });
        }
      } catch {
        arrive();
      }
    };
    const queuePan = (): void => {
      if (!live()) return;
      this.runCamera(pan);
    };
    const enqueue = (): void => {
      if (!live()) return;
      const delay = opts.delayMs ?? 0;
      if (delay <= 0) queuePan();
      else globalThis.setTimeout(() => queuePan(), delay);
    };
    if (this.map.isStyleLoaded()) enqueue();
    else this.map.once("idle", enqueue);
  }

  expandCluster(center: LngLat, zoom: number): void {
    this.runCamera(() => {
      this.map.easeTo({ center, zoom, duration: CLUSTER_EXPAND_MS });
    });
  }

  zoomBy(steps: number): void {
    this.runCamera(() => {
      this.map.easeTo({ zoom: this.map.getZoom() + steps, duration: KEY_MOVE_MS });
    });
  }

  /** Pan to the user's coordinates without dropping below the current zoom (min 14). */
  locateAt(center: LngLat, duration = KEY_MOVE_MS): void {
    this.runCamera(() => {
      this.map.easeTo({
        center,
        zoom: Math.max(this.map.getZoom(), 14),
        duration,
      });
    });
  }

  panByKeys(xDir: number, yDir: number): void {
    this.runCamera(() => {
      this.map.easeTo({ duration: KEY_MOVE_MS, offset: [-xDir * KEY_PAN_PX, -yDir * KEY_PAN_PX] });
    });
  }

  turnTo(bearing: number, pitch: number, duration: number): void {
    this.runCamera(() => {
      this.map.easeTo({ bearing, pitch, duration });
    });
  }

  /** Smoothly return to a view saved before entering edit (home browse restore). */
  restoreView(view: SavedMapView, duration = QUEST_VIEW_MS): void {
    this.runCamera(() => {
      this.map.easeTo({
        center: view.center,
        zoom: view.zoom,
        bearing: view.bearing,
        pitch: view.pitch,
        duration,
      });
    });
  }

  fitPoints(coords: LngLat[], minZoom: number): void {
    if (coords.length === 0) return;
    this.runCamera(() => {
      if (coords.length === 1) {
        this.map.easeTo({ center: coords[0], zoom: Math.max(this.map.getZoom(), minZoom), duration: FIT_MS });
        return;
      }
      const bounds = coords.reduce((b, c) => b.extend(c), new maplibregl.LngLatBounds(coords[0], coords[0]));
      this.map.fitBounds(bounds, { padding: 72, maxZoom: 17, duration: FIT_MS });
    });
  }

  fitCanvas(bounds: { west: number; south: number; east: number; north: number }, bearing: number): void {
    this.runCamera(() => {
      this.map.jumpTo({ bearing });
      this.map.fitBounds(
        [
          [bounds.west, bounds.south],
          [bounds.east, bounds.north],
        ],
        { padding: 48, maxZoom: 17, duration: FIT_MS, bearing },
      );
    });
  }

  focusSpan(sw: LngLat, ne: LngLat, point: boolean): void {
    this.runCamera(() => {
      if (point) this.map.easeTo({ center: sw, zoom: Math.max(this.map.getZoom(), 18), duration: ISSUE_FOCUS_MS });
      else this.map.fitBounds([sw, ne], { padding: 120, maxZoom: 18, duration: ISSUE_FOCUS_MS });
    });
  }
}
